import classNames from 'classnames';
import * as React from 'react';
import {
    CSSProperties,
    ChangeEventHandler,
    ComponentProps,
    ReactNode,
    forwardRef,
} from 'react';

import filterEmptyRecursive from '../../helpers/array/filterEmptyRecursive';
import surrogateId from '../../helpers/string/surrogateId';
import ErrorList from '../../types/UI/ErrorList';
import fieldStyles from '../BaseField.module.css';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Label from '../Label/Label';
import MaxWidth from '../MaxWidth/MaxWidth';
import Stack from '../Stack/Stack';

import styles from './TextField.module.css';

type TextFieldProps = Omit<
    React.HTMLProps<HTMLInputElement>,
    'label' | 'onChange' | 'prefix' | 'style' | 'children'
> & {
    label?: ReactNode;
    errors?: ErrorList;
    autoSize?: boolean;
    compact?: boolean;
    valueAlign?: 'left' | 'right';
    inputClassName?: string;
    hasErrors?: boolean;
    style?: 'default' | 'pill';
    children?: ReactNode;
    prefix?: ReactNode;
    suffix?: ReactNode;
    subdued?: boolean;
    maxInputWidth?: ComponentProps<typeof MaxWidth>['width'];
    flex?: boolean | number;
    onChange?: (value: string) => void;
    childrenAreInsideBox?: boolean;
};

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            id,
            label,
            errors = [],
            readOnly = false,
            compact = false,
            autoSize = false,
            children,
            className,
            disabled,
            valueAlign,
            inputClassName,
            onChange,
            hasErrors = false,
            style = 'default',
            width,
            flex = false,
            maxInputWidth,
            prefix,
            suffix,
            subdued,
            childrenAreInsideBox,
            onMouseDown,
            ...inputProps
        },
        ref
    ) => {
        errors = filterEmptyRecursive(errors);

        const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
            if (!onChange) {
                return;
            }

            onChange(event.target.value);
        };

        const idOrSurrogate = id ?? surrogateId();

        const hasAffix = Boolean(prefix || suffix);
        const hasChildrenNextToInput =
            !childrenAreInsideBox && Boolean(children);

        const classes = classNames(className, {
            [fieldStyles.Field]: true,
            [fieldStyles.Field_autoSize]: autoSize,
            [fieldStyles['has-errors']]: hasErrors || (errors && errors.length),
        });

        const inputWithChildrenWrapperClasses = classNames({
            [styles.InputWithChildrenWrapper]: true,
        });

        const inputWrapperClasses = classNames({
            [styles.InputWrapper]: true,
            [fieldStyles.InputWrapper]: true,
            [fieldStyles.VisualInput]: hasAffix,
            [fieldStyles.VisualInput_noBorderRadiusRight]:
                hasChildrenNextToInput,
        });

        const inputClasses = classNames(inputClassName, {
            [styles.Input]: true,
            [styles.Input_subdued]: subdued,
            [fieldStyles.Input]: true,
            [fieldStyles.Input_noLeftPadding]: Boolean(prefix),
            [fieldStyles.Input_noRightPadding]: Boolean(suffix),
            [fieldStyles.Input_alignRight]: valueAlign === 'right',
            [fieldStyles.Input_compact]: compact,
            [fieldStyles.VisualInput]: !hasAffix,
            [fieldStyles.VisualInput_noBorderRadiusRight]:
                hasChildrenNextToInput,
            [fieldStyles.VisualInput_pill]: style === 'pill',
            [fieldStyles.disabled]: disabled || readOnly,
        });

        const inputWrapper = (
            <div className={inputWithChildrenWrapperClasses}>
                <span className={inputWrapperClasses}>
                    {prefix && (
                        <label
                            className={classNames(
                                fieldStyles.Affix,
                                fieldStyles.Prefix,
                                {
                                    [fieldStyles.Affix_compact]: compact,
                                    [fieldStyles.Prefix_compact]: compact,
                                }
                            )}
                            htmlFor={idOrSurrogate}
                        >
                            {prefix}
                        </label>
                    )}

                    <input
                        ref={ref}
                        id={idOrSurrogate}
                        className={inputClasses}
                        readOnly={readOnly}
                        disabled={disabled}
                        onChange={handleChange}
                        onMouseDown={(event) => {
                            onMouseDown?.(event);

                            // Hacky solution to prevent the keyboard from opening on mobile devices
                            if (readOnly || disabled) {
                                event.preventDefault();
                            }
                        }}
                        {...inputProps}
                    />

                    {suffix && (
                        <label
                            className={classNames(
                                fieldStyles.Affix,
                                fieldStyles.Suffix,
                                {
                                    [fieldStyles.Affix_compact]: compact,
                                    [fieldStyles.Suffix_compact]: compact,
                                }
                            )}
                            htmlFor={idOrSurrogate}
                        >
                            {suffix}
                        </label>
                    )}
                </span>

                {children}
            </div>
        );

        const inputWrapperWithMaxWidth = maxInputWidth ? (
            <MaxWidth width={maxInputWidth}>{inputWrapper}</MaxWidth>
        ) : (
            inputWrapper
        );

        const errorMessage = (
            <ErrorMessage className={fieldStyles.Errors}>{errors}</ErrorMessage>
        );

        const wrapperStyle: CSSProperties = width ? { width } : {};
        if (flex) {
            wrapperStyle.flex = flex === true ? 1 : flex;
        }

        if (label) {
            return (
                <label className={classes} style={wrapperStyle}>
                    <Stack space="xsmall">
                        <Label>{label}</Label>
                        {inputWrapperWithMaxWidth}
                        {errorMessage}
                    </Stack>
                </label>
            );
        }

        return (
            <div className={classes} style={wrapperStyle}>
                <Stack space="small">
                    {inputWrapperWithMaxWidth}
                    {errorMessage}
                </Stack>
            </div>
        );
    }
);

export default TextField;
